import i18n from "../../i18n";

const API_BASE_URL =
  window.PARANOIDI_API_BASE_URL ||
  (window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "http://localhost:4000/api"
    : "/backend/api");

function getStoredPerformerSlug() {
  try {
    return (
      JSON.parse(
        window.sessionStorage.getItem("paranoidi_auth_performer") || "{}"
      )?.slug || ""
    );
  } catch {
    return "";
  }
}

function getPublicPerformerBase(performerSlug) {
  const slug = String(
    performerSlug || getStoredPerformerSlug() || ""
  ).trim();

  if (!slug) {
    return null;
  }

  return `${API_BASE_URL}/public/performers/${encodeURIComponent(slug)}`;
}

function getApiErrorMessage(fi, en) {
  const language = String(
    i18n.resolvedLanguage || i18n.language || "fi"
  ).toLowerCase();

  return language.startsWith("en") ? en : fi;
}

function createApiError(fi, en, data = null) {
  const error = new Error(getApiErrorMessage(fi, en));

  error.code = data?.code || "";
  error.payload = data || {};
  error.backendMessage = String(data?.error || "").trim();

  return error;
}

/**
 * Hakee julkisen esiintyjän tiedot backendistä.
 */
export async function getPublicPerformer(performerSlug) {
  const publicBase = getPublicPerformerBase(performerSlug);

  if (!publicBase) {
    throw createApiError(
      "Esiintyjän tunniste puuttuu",
      "Performer identifier is missing"
    );
  }

  const response = await fetch(publicBase);

  if (!response.ok) {
    throw createApiError(
      "Esiintyjän tietojen haku epäonnistui",
      "Could not load performer details"
    );
  }

  return response.json();
}

/**
 * Hakee kaikki biisitoiveet backendistä.
 * Jos performerSlug annetaan tai kirjautunut esiintyjä löytyy sessionStoragesta,
 * haku tehdään julkisesta PostgreSQL-reitistä.
 */
export async function getSongRequests(performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  const url = publicBase
    ? `${publicBase}/song-requests`
    : `${API_BASE_URL}/song-requests`;

  const response = await fetch(url);

  if (!response.ok) {
    throw createApiError(
      "Biisitoiveiden haku epäonnistui",
      "Could not load song requests"
    );
  }

  return response.json();
}

/**
 * Lisää uuden biisitoiveen.
 */
export async function createSongRequest(payload, performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  const url = publicBase
    ? `${publicBase}/song-requests`
    : `${API_BASE_URL}/song-requests`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisitoiveen lähetys epäonnistui",
      "Could not send the song request",
      data
    );
  }

  return data;
}

/**
 * Aloittaa olemassa olevan biisitoiveen maksullisen näkyvyysnoston.
 * Varsinainen maksu tapahtuu Stripe Checkoutissa.
 */
export async function createSongRequestBoostCheckout(
  id,
  performerSlug = null
) {
  const publicBase = getPublicPerformerBase(performerSlug);
  const requestId = Number(id);

  if (!Number.isInteger(requestId) || requestId < 1) {
    throw createApiError(
      "Biisitoiveen tunniste puuttuu",
      "Song request identifier is missing"
    );
  }

  const response = await fetch(
    `${publicBase}/song-requests/${encodeURIComponent(
      requestId
    )}/boost-checkout`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: "{}",
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Kappaletoiveen näkyvyysnoston maksun aloitus epäonnistui",
      "Could not start the payment for the song request boost",
      data
    );
  }

  return data;
}

/**
 * Päivittää biisitoiveen tilan.
 */
export async function updateSongRequestStatus(
  id,
  status,
  performerSlug = null
) {
  const publicBase = getPublicPerformerBase(performerSlug);

  const url = publicBase
    ? `${publicBase}/song-requests/${encodeURIComponent(id)}`
    : `${API_BASE_URL}/song-requests/${encodeURIComponent(id)}`;

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
    body: JSON.stringify({ status }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisitoiveen päivitys epäonnistui",
      "Could not update the song request",
      data
    );
  }

  return data;
}

/**
 * Poistaa biisitoiveen.
 */
export async function deleteSongRequest(id, performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  const url = publicBase
    ? `${publicBase}/song-requests/${encodeURIComponent(id)}`
    : `${API_BASE_URL}/song-requests/${encodeURIComponent(id)}`;

  const response = await fetch(url, {
    method: "DELETE",
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisitoiveen poisto epäonnistui",
      "Could not delete the song request",
      data
    );
  }

  return data;
}

/**
 * Poistaa kaikki biisitoiveet legacy-reitistä.
 * SaaS-hallinnassa yksittäiset poistot käyttävät performer-kohtaista reittiä.
 */
export async function clearSongRequests() {
  const response = await fetch(`${API_BASE_URL}/song-requests`, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw createApiError(
      "Biisitoiveiden tyhjennys epäonnistui",
      "Could not clear the song requests"
    );
  }

  return response.json();
}

/**
 * Hakee Paranoidin biisikirjaston backendistä.
 * Jos performerSlug annetaan tai kirjautunut esiintyjä löytyy sessionStoragesta,
 * haku tehdään julkisesta PostgreSQL-reitistä.
 */
export async function getSongs(performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);
  const url = publicBase ? `${publicBase}/songs` : `${API_BASE_URL}/songs`;

  const response = await fetch(url);

  if (!response.ok) {
    throw createApiError(
      "Biisikirjaston haku epäonnistui",
      "Could not load the song library"
    );
  }

  return response.json();
}

/**
 * Lisää monta biisiä kerralla Paranoidin biisikirjastoon.
 */
export async function bulkCreateSongs(payload, performerSlug = null) {
  const slug = String(
    performerSlug || getStoredPerformerSlug() || ""
  ).trim();

  const url = slug
    ? `${API_BASE_URL}/admin/performers/${encodeURIComponent(slug)}/songs/bulk`
    : `${API_BASE_URL}/songs/bulk`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisien massa-ajo epäonnistui",
      "Could not add the songs",
      data
    );
  }

  return data;
}

/**
 * Päivittää yksittäisen biisin Paranoidin biisikirjastossa.
 */
export async function updateSong(id, payload, performerSlug = null) {
  const slug = String(
    performerSlug || getStoredPerformerSlug() || ""
  ).trim();

  const url = slug
    ? `${API_BASE_URL}/admin/performers/${encodeURIComponent(
        slug
      )}/songs/${encodeURIComponent(id)}`
    : `${API_BASE_URL}/songs/${encodeURIComponent(id)}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisin päivitys epäonnistui",
      "Could not update the song",
      data
    );
  }

  return data;
}

/**
 * Poistaa biisin Paranoidin biisikirjastosta.
 */
export async function deleteSong(id, performerSlug = null) {
  const slug = String(
    performerSlug || getStoredPerformerSlug() || ""
  ).trim();

  const url = slug
    ? `${API_BASE_URL}/admin/performers/${encodeURIComponent(
        slug
      )}/songs/${encodeURIComponent(id)}`
    : `${API_BASE_URL}/songs/${encodeURIComponent(id)}`;

  const response = await fetch(url, {
    method: "DELETE",
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Biisin poisto epäonnistui",
      "Could not delete the song",
      data
    );
  }

  return data;
}

/**
 * Hakee yhdelle biisille metatietoehdotuksen verkosta.
 */
export async function enrichSong(id, payload = {}) {
  const response = await fetch(`${API_BASE_URL}/songs/${id}/enrich`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw createApiError(
      "Biisin tietojen haku epäonnistui",
      "Could not fetch song details"
    );
  }

  return response.json();
}

export async function previewSongImportFile(file) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE_URL}/songs/import-file-preview`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw createApiError(
      "Tiedoston esikatselu epäonnistui.",
      "Could not preview the file."
    );
  }

  return response.json();
}

export async function enrichSongImportPreview(payload) {
  const response = await fetch(`${API_BASE_URL}/songs/import-preview-enrich`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw createApiError(
      "Esikatselun tietojen haku epäonnistui.",
      "Could not fetch details for the preview."
    );
  }

  return response.json();
}

/**
 * Hakee julkisella Paranoidi-sivulla voimassa olevat tapahtumat.
 */
export async function getPublicPerformerEvents(performerSlug = null) {
  const publicBase = getPublicPerformerBase(performerSlug);

  if (!publicBase) {
    throw createApiError(
      "Esiintyjän tunniste puuttuu",
      "Performer identifier is missing"
    );
  }

  let response = await fetch(`${publicBase}/events`);

  if (response.status === 404) {
    const slug = String(
      performerSlug || getStoredPerformerSlug() || ""
    ).trim();

    response = await fetch(
      `${API_BASE_URL}/performers/${encodeURIComponent(slug)}/events`
    );
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw createApiError(
      "Tapahtumien haku epäonnistui",
      "Could not load events",
      data
    );
  }

  return data || { ok: true, items: [] };
}